import React from 'react';
import { Transition } from '@headlessui/react';
import {
  WifiIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { useOfflineStatus } from './ErrorBoundary';
import { useAccessibility } from '@/contexts/AccessibilityContext';
import { cn } from '@/utils/cn';

interface OfflineIndicatorProps {
  className?: string;
  position?: 'top' | 'bottom';
}

export const OfflineIndicator: React.FC<OfflineIndicatorProps> = ({
  className,
  position = 'top',
}) => {
  const isOffline = useOfflineStatus();
  const { announce } = useAccessibility();
  const [wasOffline, setWasOffline] = React.useState(false);

  React.useEffect(() => {
    if (isOffline && !wasOffline) {
      setWasOffline(true);
      announce('You are now offline. Some features may not work.', 'assertive');
    } else if (!isOffline && wasOffline) {
      announce(
        'Connection restored. All features are now available.',
        'polite'
      );
      // Keep showing the "back online" message briefly
      setTimeout(() => setWasOffline(false), 3000);
    }
  }, [isOffline, wasOffline, announce]);

  const showIndicator = isOffline || (wasOffline && !isOffline);

  return (
    <Transition
      show={showIndicator}
      enter="transform ease-out duration-300 transition"
      enterFrom={position === 'top' ? '-translate-y-full' : 'translate-y-full'}
      enterTo="translate-y-0"
      leave="transition ease-in duration-200"
      leaveFrom="translate-y-0"
      leaveTo={position === 'top' ? '-translate-y-full' : 'translate-y-full'}
    >
      <div
        className={cn(
          'fixed left-0 right-0 z-50 px-4 py-2',
          position === 'top' ? 'top-0' : 'bottom-0',
          className
        )}
        role="status"
        aria-live="polite"
      >
        <div
          className={cn(
            'mx-auto max-w-md rounded-lg border px-4 py-3 shadow-lg',
            isOffline
              ? 'bg-warning-900 border-warning-600 text-warning-100'
              : 'bg-success-900 border-success-600 text-success-100'
          )}
        >
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              {isOffline ? (
                <ExclamationTriangleIcon
                  className="h-5 w-5"
                  aria-hidden="true"
                />
              ) : (
                <CheckCircleIcon className="h-5 w-5" aria-hidden="true" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium">
                {isOffline ? 'You are offline' : 'Back online'}
              </p>
              <p className="text-xs opacity-90">
                {isOffline
                  ? 'Some features may not work until connection is restored.'
                  : 'All features are now available.'}
              </p>
            </div>
            <div className="flex-shrink-0">
              <WifiIcon
                className={cn(
                  'h-5 w-5',
                  isOffline ? 'opacity-50' : 'opacity-100'
                )}
                aria-hidden="true"
              />
            </div>
          </div>
        </div>
      </div>
    </Transition>
  );
};

// Hook to get network status with additional info
export const useNetworkStatus = () => {
  const isOffline = useOfflineStatus();
  const [connectionType, setConnectionType] = React.useState<string>('unknown');
  const [effectiveType, setEffectiveType] = React.useState<string>('unknown');

  React.useEffect(() => {
    // @ts-ignore - navigator.connection is not in TypeScript types
    const connection =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    if (connection) {
      const updateConnectionInfo = () => {
        setConnectionType(connection.type || 'unknown');
        setEffectiveType(connection.effectiveType || 'unknown');
      };

      updateConnectionInfo();
      connection.addEventListener('change', updateConnectionInfo);

      return () => {
        connection.removeEventListener('change', updateConnectionInfo);
      };
    }
  }, []);

  return {
    isOffline,
    isOnline: !isOffline,
    connectionType,
    effectiveType,
    isSlowConnection: effectiveType === 'slow-2g' || effectiveType === '2g',
  };
};
